import type { CaseSheetClinicalView, ClinicalPrescriptionLineView, ClinicalPrescriptionView } from '@/dashboard/api';

export const PRESCRIPTION_FIELDS = [
  ['name', 'Medicine', 300], ['strength', 'Strength', 120], ['dose', 'Dose', 120],
  ['route', 'Route', 120], ['frequency', 'How often', 120], ['duration', 'Days', 120],
  ['instructions', 'Instructions', 2000],
] as const;
export const emptyPrescription = (): ClinicalPrescriptionView => ({ version: 1, noMedicinesToday: false, medicines: [] });
export function newMedicine(id: string, name = ''): ClinicalPrescriptionLineView {
  return { id, name, strength: '', dose: '', route: '', frequency: '', duration: '', instructions: '' };
}
/** Whitespace equality matches the server parser. Nothing is inferred from a
 * name; an empty document stays different from an explicit no-medicines fact. */
export function normalizePrescription(value: ClinicalPrescriptionView | null): ClinicalPrescriptionView | null {
  return value === null ? null : { version: 1, noMedicinesToday: value.noMedicinesToday,
    medicines: value.medicines.map(line => ({ id: line.id.toLowerCase(),
      name: line.name.trim(), strength: line.strength.trim(), dose: line.dose.trim(), route: line.route.trim(),
      frequency: line.frequency.trim(), duration: line.duration.trim(), instructions: line.instructions.trim(),
      ...(line.repeatedFrom ? { repeatedFrom: { caseSheetId: line.repeatedFrom.caseSheetId, revision: line.repeatedFrom.revision, medicineId: line.repeatedFrom.medicineId.toLowerCase() } } : {}) })) };
}
export function samePrescription(a: ClinicalPrescriptionView | null, b: ClinicalPrescriptionView | null): boolean {
  return JSON.stringify(normalizePrescription(a)) === JSON.stringify(normalizePrescription(b));
}
export function prescriptionProblems(value: ClinicalPrescriptionView | null | undefined): string[] {
  if (!value) return ['Medicines not recorded.'];
  if (value.noMedicinesToday) return value.medicines.length ? ['Review the medicines before printing.'] : [];
  if (!value.medicines.length) return ['Add a medicine or choose No medicines today.'];
  return value.medicines.flatMap((line, index) => {
    const fields = (['name', 'dose', 'frequency', 'duration'] as const).filter(key => !line[key].trim());
    const labels = { name: 'medicine name', dose: 'dose', frequency: 'how often', duration: 'days' };
    return fields.length ? [`Medicine ${index + 1}: add ${fields.map(key => labels[key]).join(', ')}.`] : [];
  });
}
/** Only this independent writer's fields are installed. A medicine save must
 * never replace pending clinical notes, plan edits or the patient profile. */
export function prescriptionFields(sheet: CaseSheetClinicalView) {
  return { prescription: sheet.prescription ?? null, prescriptionRevision: sheet.prescriptionRevision ?? 0,
    prescriptionRecordedAt: sheet.prescriptionRecordedAt ?? null,
    prescriptionActorStaffUserId: sheet.prescriptionActorStaffUserId ?? null };
}

/** Frozen warning: literal recorded names only. Unknowns, legacy prose, drug
 * families and cross-reactivity are never inferred or treated as a veto. */
export function prescriptionAllergyMatches(name: string, substances: readonly {readonly source: string; readonly label: string}[]): string[] {
  const entered = name.trim().toLocaleLowerCase();
  return [...new Set(substances.filter(term => (term.source === 'entered' || term.source === 'option') && term.label.trim() && entered.includes(term.label.trim().toLocaleLowerCase())).map(term => term.label.trim()))];
}
